<template>
  <div class="search-suggestion-dropdown" :class="`search-suggestion-dropdown--${variant}`">
    <div v-if="loading" class="search-suggestion-dropdown__loading">
      <a-spin :size="loadingSize" />
      <span v-if="loadingText">{{ loadingText }}</span>
    </div>

    <button
      v-else-if="error"
      type="button"
      class="search-suggestion-dropdown__retry"
      @click="emit('retry')"
    >
      联想加载失败，点击重试
    </button>

    <template v-else-if="items.length > 0">
      <button
        v-for="(item, index) in items"
        :key="getSuggestionItemKey(item, index)"
        type="button"
        class="search-suggestion-dropdown__item"
        :title="getItemLabel(item)"
        @mousedown.prevent="handleSelect(item, index)"
        @click="handleSelect(item, index)"
      >
        <SvgIcon name="search" :size="iconSize" class="search-suggestion-dropdown__icon" />
        <span class="search-suggestion-dropdown__text">
          <template v-if="highlightKeyword">
            <template
              v-for="(part, partIndex) in getHighlightedParts(getItemLabel(item))"
              :key="partIndex"
            >
              <mark v-if="part.matched">{{ part.text }}</mark>
              <template v-else>{{ part.text }}</template>
            </template>
          </template>
          <template v-else>{{ getItemLabel(item) }}</template>
        </span>
      </button>
    </template>

    <div v-else class="search-suggestion-dropdown__empty">
      <SvgIcon
        v-if="showEmptyIcon"
        name="search"
        :size="emptyIconSize"
        class="search-suggestion-dropdown__empty-icon"
      />
      <span>{{ emptyText }}</span>
    </div>
  </div>
</template>

<script setup>
/**
 * @description 通用搜索联想下拉，负责 loading、空态、关键词高亮与选中事件。
 */
import { computed, ref } from 'vue'
import { getSuggestionItemKey } from '@/utils/searchSuggestion'

const props = defineProps({
  keyword: {
    type: String,
    default: '',
  },
  items: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loadingText: {
    type: String,
    default: '',
  },
  highlightKeyword: {
    type: Boolean,
    default: true,
  },
  variant: {
    type: String,
    default: 'desktop',
    validator: (value) => ['desktop', 'global', 'mobile'].includes(value),
  },
  emptyText: {
    type: String,
    default: '暂无数据',
  },
  showEmptyIcon: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['select', 'retry'])
const selectedTickKey = ref('')

const iconSize = computed(() => (['global', 'mobile'].includes(props.variant) ? 16 : 18))
const loadingSize = computed(() => (props.variant === 'global' ? 16 : 20))
const emptyIconSize = computed(() => (props.variant === 'mobile' ? 20 : 18))

const getItemLabel = (item) => String(item?.label ?? '')

const getHighlightedParts = (text) => {
  const source = String(text || '')
  const target = props.keyword.trim()
  if (!target) return [{ text: source, matched: false }]

  const index = source.toLowerCase().indexOf(target.toLowerCase())
  if (index < 0) return [{ text: source, matched: false }]

  return [
    { text: source.slice(0, index), matched: false },
    { text: source.slice(index, index + target.length), matched: true },
    { text: source.slice(index + target.length), matched: false },
  ].filter((part) => part.text)
}

const handleSelect = (item, index) => {
  const key = getSuggestionItemKey(item, index)
  if (selectedTickKey.value === key) return
  selectedTickKey.value = key
  emit('select', item)
  globalThis.setTimeout(() => {
    selectedTickKey.value = ''
  }, 200)
}
</script>

<style lang="scss" scoped>
.search-suggestion-dropdown {
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  background: $bg-white;

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    border: 0;
    background: transparent;
    color: $text-title-color;
    text-align: left;
    cursor: pointer;
    box-sizing: border-box;

    mark {
      padding: 0;
      background: transparent;
      color: $primary-color;
    }

    &:hover {
      color: $primary-color;
    }
  }

  &__icon {
    flex: 0 0 auto;
    color: $text-caption-color;
  }

  &__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    // 普通 HTML 会折叠连续空格；搜索结果名称中的空格属于业务文本，需要原样展示。
    white-space: pre;
  }

  &__loading,
  &__empty,
  &__retry {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: $text-subtitle-color;
    text-align: center;
  }

  &__empty-icon {
    color: $text-placeholder-color;
  }

  &__retry {
    width: 100%;
    border: 0;
    background: transparent;
    cursor: pointer;
    &:hover,
    &:focus-visible {
      color: $primary-color;
    }
  }

  &--desktop {
    gap: 16px;
    max-height: min(400px, calc(100vh - 208px));
    padding: 16px;
    overflow-y: auto;
    border-bottom: 1px solid $border-light-color;
    border-radius: $radius-6;
    box-shadow: $shadow-dropdown-level1;

    .search-suggestion-dropdown__item {
      min-height: 20px;
      padding: 0;
      font-size: $font-size-title-3;
      line-height: 1.4;
    }

    .search-suggestion-dropdown__loading,
    .search-suggestion-dropdown__empty,
    .search-suggestion-dropdown__retry {
      min-height: 56px;
      font-size: $font-size-body-1;
      line-height: $line-height-body-1;
    }
  }

  &--global {
    gap: 0;

    .search-suggestion-dropdown__item {
      gap: 12px;
      min-height: 48px;
      padding: 12px 0;
      border-bottom: 1px solid $border-light-color;
      font-size: 16px;
      line-height: 24px;

      &:last-child {
        border-bottom: 0;
      }

      &:hover {
        .search-suggestion-dropdown__icon {
          color: $primary-color;
        }
      }
    }

    .search-suggestion-dropdown__icon {
      color: $text-subtitle-color;
    }

    .search-suggestion-dropdown__loading,
    .search-suggestion-dropdown__empty,
    .search-suggestion-dropdown__retry {
      justify-content: center;
      min-height: 88px;
      padding: 0 16px;
      box-sizing: border-box;
      font-size: 14px;
      line-height: 20px;
    }
  }

  &--mobile {
    gap: 0;
    padding: 0;

    .search-suggestion-dropdown__item {
      min-height: 36px;
      padding: 8px 16px;
      font-size: 13px;
      line-height: 20px;
    }

    .search-suggestion-dropdown__icon {
      color: $text-placeholder-color;
    }

    .search-suggestion-dropdown__loading {
      min-height: 112px;
    }

    .search-suggestion-dropdown__empty,
    .search-suggestion-dropdown__retry {
      flex-direction: column;
      gap: 8px;
      min-height: 112px;
      padding: 16px 0;
      font-size: 13px;
      line-height: 20px;
    }
  }
}
</style>
